/**
 * TopicConfig — contact form topic presets + field configurations
 *
 * All 11 topics discovered from non-prod environment (custdemo.rocktools.my.sandvik).
 * ALL topics now use the single-page modal template (new Sandvik approved layout):
 *   "Almost there! Do a quick check"
 *   Two-column: Your needs | Your details
 *
 * The old 3-step wizard is deprecated — replaced during the MRT PWA rebuild.
 *
 * Topics with prePopulate: true have their message field pre-filled
 * from the user's PLP/PDP interaction context (e.g. "I would like to purchase this {SKU}...").
 */

export const TOPIC_FIELDS = {
  subject: { label: 'Subject', type: 'text', placeholder: 'Brief description of your enquiry', required: true },
  message: { label: 'Message', type: 'textarea', placeholder: 'Please describe your request in detail...', required: true },
  product: { label: 'Product', type: 'text', placeholder: 'Product name or SKU', required: true },
  quantity: { label: 'Quantity', type: 'number', placeholder: 'Number of items', required: true, min: 1 },
  company: { label: 'Company name', type: 'text', placeholder: 'Your company name', required: true },
  country: { label: 'Country', type: 'text', placeholder: 'Country', required: true },
  manufacturer: { label: 'Manufacturer', type: 'text', placeholder: 'Rig/drill manufacturer', required: true },
  model: { label: 'Model', type: 'text', placeholder: 'Model name or number', required: true },
  orderNumber: { label: 'Order number', type: 'text', placeholder: 'e.g. P006375', required: true },
  lineItems: { label: 'Line items', type: 'textarea', placeholder: 'List the items you wish to return', required: true },
  reason: { label: 'Reason for return', type: 'textarea', placeholder: 'Please explain the reason', required: true },
};

export const CONTACT_DETAILS_FIELDS = [
  { key: 'firstName', label: 'First name', type: 'text', required: true },
  { key: 'lastName', label: 'Last name', type: 'text', required: true },
  { key: 'email', label: 'Email', type: 'email', required: true },
  { key: 'phone', label: 'Phone', type: 'tel', required: false },
  { key: 'company', label: 'Company', type: 'text', required: true },
  { key: 'country', label: 'Country', type: 'text', required: true },
  { key: 'postalCode', label: 'Postal code', type: 'text', required: false },
];

/**
 * All 11 contact topics — unified modal form.
 * Order matches live app left-drawer contact menu.
 */
export const CONTACT_TOPICS = [
  {
    id: 'others',
    label: 'Others',
    fields: ['subject', 'message'],
    prePopulate: false,
    description: 'General enquiry not covered by other topics.',
  },
  {
    id: 'product-info',
    label: 'Product information',
    fields: ['product', 'message'],
    prePopulate: true,
    description: 'Request detailed information about a product.',
  },
  {
    id: 'become-customer',
    label: 'Become a Sandvik Customer',
    fields: ['company', 'country', 'message'],
    prePopulate: false,
    description: 'Apply to become a registered Sandvik customer.',
  },
  {
    id: 'request-order',
    label: 'Request order for N items',
    fields: ['product', 'quantity', 'message'],
    prePopulate: true,
    description: 'Submit an order request for specific products.',
  },
  {
    id: 'request-availability',
    label: 'Request for Availability',
    fields: ['product', 'quantity', 'message'],
    prePopulate: true,
    description: 'Check availability of products.',
  },
  {
    id: 'product-sellability',
    label: 'Product sellability enquiry',
    fields: ['product', 'message'],
    prePopulate: true,
    description: 'Enquire about product sellability in your region.',
  },
  {
    id: 'missing-rig',
    label: "Can't find rig/model",
    fields: ['manufacturer', 'model', 'message'],
    prePopulate: false,
    description: "Report a missing rig or rig model from the catalog.",
  },
  {
    id: 'missing-drill',
    label: "Can't find rock drill/model",
    fields: ['manufacturer', 'model', 'message'],
    prePopulate: false,
    description: "Report a missing rock drill or model from the catalog.",
  },
  {
    id: 'order-issue',
    label: 'Order issue',
    fields: ['orderNumber', 'message'],
    prePopulate: false,
    description: 'Report an issue with an existing order.',
  },
  {
    id: 'return-request',
    label: 'Return request',
    fields: ['orderNumber', 'lineItems', 'reason'],
    prePopulate: false,
    description: 'Request a return for ordered items.',
  },
  {
    id: 'request-alternate',
    label: 'Request for alternate product',
    fields: ['product', 'message'],
    prePopulate: true,
    description: 'Request an alternative product recommendation.',
  },
];

/**
 * Get pre-populated message for a topic based on user context.
 * Product-info / product-sellability topics carry structured header lines
 * (Part number + Description) so the sales rep sees the identifiers at a
 * glance without hunting for them; user then adds their query below.
 *
 * @param {string} topicId
 * @param {Object} context - { productName, sku, quantity }
 * @returns {string} Pre-populated message text
 */
export function getPrePopulatedMessage(topicId, context = {}) {
  const { productName, sku, quantity } = context;
  const productRef = productName || sku || 'the selected product';

  // Structured header used by product-info / product-sellability enquiries.
  // Format is a labelled block so it grep-parses cleanly on the receiving side.
  const productHeaderBlock = () => {
    const lines = [];
    if (sku) lines.push(`Part number: ${sku}`);
    if (productName) lines.push(`Description: ${productName}`);
    lines.push('', 'Enquiry:', '[Please describe your query here]');
    return lines.join('\n');
  };

  switch (topicId) {
    case 'request-order':
      return `I would like to purchase ${quantity || 'N'} unit(s) of ${productRef}.`;
    case 'request-availability':
      return `I would like to check the availability of ${productRef}${quantity ? ` (quantity: ${quantity})` : ''}.`;
    case 'request-alternate':
      return `I am looking for an alternative to ${productRef}. Could you recommend a suitable replacement?`;
    case 'product-info':
    case 'product-sellability':
      return productHeaderBlock();
    default:
      return '';
  }
}
